import { ApolloError, useMutation } from '@apollo/client';
import { useBreakpoint } from '@moda/om';
import { deepRemoveKeys } from '@moda/portal-stanchions';
import { StatusCodes } from 'http-status-codes';
import { useCallback, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useEmailSignupPhoneNumber } from '../../components/EmailSignup/useEmailSignupPhoneNumber';
import { Gender } from '../../generated/types/globalTypes';
import { reportError } from '../../lib/reportError';
import { tc } from '../../lib/trackingContext';
import { useEmailVerification } from '../useEmailVerification';
import { usePhoneNumberValidation } from '../usePhoneNumberValidation';
import { AttentiveSignUpSourceId } from './attentiveSignUpSourceId';
import {
  AttentiveSignUpSource,
  ATTENTIVE_ERROR_RESPONSE_CODES,
  EmailSubscribeMutation,
  EmailSubscribeMutationVariables,
  EMAIL_SUBSCRIBE_MUTATION,
  Placement,
  RestEmailSubscribeTableAttentiveResponseTableContent
} from './useEmailSubscribe.rest';

const isAttentiveErrorStatusCode = (statusCode: StatusCodes) =>
  ATTENTIVE_ERROR_RESPONSE_CODES.includes(statusCode);

const isAttentiveStatusCodeAccepted = (statusCode: StatusCodes) =>
  statusCode === StatusCodes.ACCEPTED;

export const useAttentiveSubscribe = () => {
  const { vertical = 'women' } = useParams<{
    vertical: Gender | undefined;
    trunkshowSlug: string | undefined;
    designerSlug: string | undefined;
  }>();
  const { enabled: phoneNumberEnabled, phone, setPhone } = useEmailSignupPhoneNumber();

  const isMobile = useBreakpoint({ lt: 'md' }).matches;

  const { isValid: isEmailValid, error: mailValidationError } = useEmailVerification();
  const { isValid: isPhoneNumberValid, error: phoneNumberValidationError } =
    usePhoneNumberValidation();

  const [mutate, { data, loading, error: mojoError }] = useMutation<
    EmailSubscribeMutation,
    EmailSubscribeMutationVariables
  >(EMAIL_SUBSCRIBE_MUTATION);

  const attentiveError = useMemo(() => {
    if (!data) return;

    const { statusCode } = data.emailSubscribe.table.attentiveResponse.table;

    if (!isAttentiveErrorStatusCode(Number(statusCode))) return;

    try {
      const parsedError: RestEmailSubscribeTableAttentiveResponseTableContent = JSON.parse(
        data.emailSubscribe.table.attentiveResponse.table.content
      );
      return parsedError;
    } catch {
      return data.emailSubscribe.table.attentiveResponse.table.content;
    }
  }, [data]);

  const handleError = useCallback((caughtError: ApolloError) => reportError(caughtError), []);

  const [isSubscribed, setIsSubscribed] = useState(false);

  const subscribe = useCallback(
    (email: string, label: Placement, phone: string) => {
      if (!isEmailValid(email)) return;
      if (!isPhoneNumberValid(phone)) return;

      const source: AttentiveSignUpSource = isMobile ? 'mobile' : 'desktop';
      const filteredPhoneNumber = phone.replace(/[^0-9]/g, '');

      mutate({
        variables: {
          input: {
            user: {
              email,
              gender: Gender[vertical],
              phone: filteredPhoneNumber
            },
            label,
            category: 'Marketing Signup',
            sign_up_source: source,
            sign_up_source_id: AttentiveSignUpSourceId[source]
          }
        }
      })
        .then(({ data }) => {
          if (!data) return;

          const { statusCode } = data.emailSubscribe.table.attentiveResponse.table;

          tc.track('Attentive Signup Submitted', {
            addToTrackingEvent: {
              attentiveResponse: {
                statusCode,
                content: deepRemoveKeys(
                  data.emailSubscribe.table.attentiveResponse.table.content,
                  '__typename'
                )
              }
            }
          });

          if (isAttentiveStatusCodeAccepted(Number(statusCode))) {
            setIsSubscribed(true);
          }
        })
        .catch(handleError);
    },
    [handleError, isEmailValid, isMobile, isPhoneNumberValid, mutate, vertical]
  );

  const error = attentiveError || (mojoError && 'Unable to subscribe you at this time');

  return {
    subscribe,
    loading,
    error,
    mailValidationError,
    attentiveError,
    isSubscribed,
    phoneNumberEnabled,
    phone,
    setPhone,
    isEmailValid,
    isPhoneNumberValid,
    phoneNumberValidationError
  };
};
